import { VFXEffectConfig } from '../../VFXEffectRegistry';
import { glintScatter, groundCloud, ringBlast, risingColumn, shardBurst, starFlash, wispRise } from './burstBuilders';
import { driftMotes, orbiter, pulsingShell } from './statusAuraBuilders';
import { CLASS_MOVEMENT_CONFIG } from '../../../../config/abilities/classMovement.config';
import { PLAYER_HITBOX } from '../../../../shared/logic/CombatAnchors';
import { PACT_DOOR_LOOK } from '../../../../config/effects/pactDoorLook.config';

// =============================================================================
// MOVEMENT ABILITY VFX — the class movement abilities picked from
// docs/plans/2026-10-07-fun-and-polish/04a-MOVEMENT-ABILITY-CONCEPTS.md (D13).
//
// Anchors: the origin/destination defs are spawned by DashBehavior with
// `vfxAnchor: 'center'`, so (0,0) is the CENTRE of the 40×40 hitbox and the
// feet are at +FEET_Y. Directional defs are authored RIGHT-facing; flipX
// mirrors beams for left-facing casts. DashTravelState landings and the
// VFXSystem ghost fallback also supply this body-centre anchor.
//
// Each ability's TOML `cast_effect` is a SYMMETRIC def: remote observers
// replay the cast effect without a facing.
// =============================================================================

const FEET_Y = PLAYER_HITBOX.height / 2;
const LANDING = { offsetX: 0, offsetY: FEET_Y } as const;

// ── Shaman — Stormstride ────────────────────────────────────────────────────

/** Shaman lightning palette (04a §2): teal calling colour + lightning blue. */
const STORM = { teal: 0x00B3A4, bolt: 0x88DDFF, white: 0xE6FFFF } as const;

/** The crack at each end of the dash: a teal ring snap, a four-point flash
 *  and scattered sparks. */
const STORM_CRACK = [
  ringBlast({ radius: 14, color: STORM.teal, alpha: 0.8, glow: true, depth: 95,
    stroke: { color: STORM.bolt, width: 2 }, expandTo: 2.4, durationMs: 220 }),
  starFlash({ points: 4, innerRadius: 3, outerRadius: 16, color: STORM.white, alpha: 0.95,
    glow: true, depth: 97, durationMs: 180 }),
  glintScatter({ count: 7, spread: 30, color: STORM.bolt, radius: 2, durationMs: 240 }),
];

/**
 * Stormstride bolt — a jagged lightning line from the shaman to where the
 * dash ends (exactly the dash distance), gone in ~200 ms, plus the origin
 * crack. Right-facing basis; flipX mirrors the beams.
 */
const STORMSTRIDE_BOLT: VFXEffectConfig = {
  id: 'stormstride_bolt',
  name: 'Stormstride Bolt',
  category: 'burst',
  description: 'Jagged lightning line along the Stormstride dash',
  primitives: [
    {
      type: 'beam', beamLength: CLASS_MOVEMENT_CONFIG.stormstride.distancePx, beamWidth: 5,
      beamSegments: 9, lineJitter: 22,
      color: STORM.bolt, alpha: 0.95, depth: 97, glow: true,
      tween: { alpha: 0, duration: 220, ease: 'Power2' },
    },
    // A thinner white fork so the line reads as lightning, not a laser.
    {
      type: 'beam', beamLength: CLASS_MOVEMENT_CONFIG.stormstride.distancePx, beamWidth: 2,
      beamSegments: 12, lineJitter: 30,
      color: STORM.white, alpha: 0.85, depth: 98,
      tween: { alpha: 0, duration: 160, ease: 'Power2', delay: 30 },
    },
    ...STORM_CRACK,
  ],
};

/** Stormstride spark — the arrival crack (and the remote cast beat). */
const STORMSTRIDE_SPARK: VFXEffectConfig = {
  id: 'stormstride_spark',
  name: 'Stormstride Spark',
  category: 'burst',
  description: 'Electric crack where Stormstride lands',
  primitives: [...STORM_CRACK],
};

/** Stormstride trail fallback — one blue spark at the body centre, replayed
 *  per afterimage only when the ghost pool cannot spawn one (VFXSystem.
 *  onDashGhost; spawned at the body centre). Without it the fallback was
 *  the whole bolt def — two dash-length beams and a crack per afterimage. */
const STORMSTRIDE_GLINT: VFXEffectConfig = {
  id: 'stormstride_glint',
  name: 'Stormstride Glint',
  category: 'burst',
  description: 'Blue spark along the Stormstride dash (afterimage fallback)',
  primitives: [
    { type: 'circle', radius: 3,
      color: STORM.bolt, alpha: 0.85, depth: 94, glow: true,
      tween: { alpha: 0, scale: 0.4, duration: 180, ease: 'Power2' } },
  ],
};

// ── Druid — Harebound ───────────────────────────────────────────────────────

/** Druid palette (DruidVFX doctrine): olive roots, nature green, pale spores. */
const HARE = { olive: 0x6B8E23, green: 0x44CC44, spore: 0xCCFFAA } as const;

/** Leaf-diamond sliver — pointed both ends, reads as foliage (DruidVFX). */
const LEAF = { points: [0, -7, 3, 0, 0, 5, -3, 0] };

/** Harebound spring — the kick-off at the feet: leaves flung out, a small
 *  green snap and spores lifting. Symmetric (also the remote cast beat). */
const HAREBOUND_SPRING: VFXEffectConfig = {
  id: 'harebound_spring',
  name: 'Harebound Spring',
  category: 'burst',
  description: 'Leaf kick and spores where a Harebound bound takes off',
  primitives: [
    { ...ringBlast({ radius: 10, color: HARE.green, alpha: 0.7, glow: true, depth: 94,
      expandTo: 2.2, durationMs: 240 }), offsetY: FEET_Y - 4 },
    { ...shardBurst({ count: 6, spread: 18, color: HARE.olive, alpha: 0.95, depth: 95,
      shard: LEAF, travel: 46, durationMs: 380 }), offsetY: FEET_Y - 6 },
    { ...wispRise({ count: 4, spread: 14, radius: 2.5, color: HARE.spore, glow: true,
      travel: 26, durationMs: 420, depth: 96 }), offsetY: FEET_Y - 8 },
  ],
};

/** Harebound landing — grass at the feet, relative to the landing body centre. */
const HAREBOUND_LANDING: VFXEffectConfig = {
  id: 'harebound_landing',
  name: 'Harebound Landing',
  category: 'burst',
  description: 'Ring of grass where a Harebound bound lands',
  primitives: [
    { ...groundCloud({ width: 56, height: 12, color: HARE.olive, alpha: 0.55, depth: 93,
      swellTo: 1.6, durationMs: 420 }), ...LANDING },
    { ...shardBurst({ count: 5, spread: 16, color: HARE.green, alpha: 0.9, depth: 95,
      shard: { width: 2, height: 9 }, travel: 30, durationMs: 300 }),
    offsetX: LANDING.offsetX, offsetY: LANDING.offsetY - 4 },
  ],
};

/** Harebound trail fallback — one leaf at the feet, replayed per afterimage
 *  only when the ghost pool cannot spawn one (spawned at the body centre).
 *  Without it the fallback was the whole spring def. */
const HAREBOUND_LEAF: VFXEffectConfig = {
  id: 'harebound_leaf',
  name: 'Harebound Leaf',
  category: 'burst',
  description: 'Leaf at the feet along a Harebound bound (afterimage fallback)',
  primitives: [
    { type: 'ellipse', width: 6, height: 3, ...LANDING,
      color: HARE.olive, alpha: 0.85, depth: 93,
      tween: { alpha: 0, scale: 0.5, duration: 240, ease: 'Power2' } },
  ],
};

// ── Warlock — Pact Door ─────────────────────────────────────────────────────

/** Warlock palette doctrine (WarlockVFX): fel green, shadow purple, demon
 *  ember lantern, pact crimson sparks. */
const PACT = { fel: 0x66FF44, purple: 0x551188, ember: 0xCC3311, crimson: 0xB3122E } as const;

/**
 * Pact Door — the cast beat OTHER players see (the TOML cast_effect). The
 * caster's own doors, which stand open for the return window and swing
 * shut, are pooled rigs in PactDoorVFXHandler (D24); this one-shot is the
 * same door in brief: a fel frame on the feet with an ember lantern, whose
 * crimson leaf swings open past edge-on as it fades. Same proportions as
 * PACT_DOOR_LOOK (src/config/effects/pactDoorLook.config.ts).
 */
function pactDoor(holdMs: number, fadeMs: number): VFXEffectConfig['primitives'] {
  const W = PACT_DOOR_LOOK.width;
  const H = PACT_DOOR_LOOK.height;
  const P = PACT_DOOR_LOOK.post;
  const L = PACT_DOOR_LOOK.lintel;
  const lintel = FEET_Y - H;
  const fade = { alpha: 0, duration: fadeMs, delay: holdMs, ease: 'Power2' } as const;
  const post = (side: 1 | -1) => ({
    type: 'rect' as const, width: P, height: H, offsetX: side * (W / 2 - P / 2), offsetY: FEET_Y - H / 2,
    color: PACT.fel, alpha: 0.95, depth: 94, glow: true, tween: fade,
  });
  return [
    {
      type: 'rect', width: W - 2 * P, height: H - L, offsetY: FEET_Y - (H - L) / 2,
      color: PACT.purple, alpha: 0.9, depth: 92, tween: fade,
    },
    {
      // The leaf, hinged on the left post: swings open past edge-on.
      type: 'polygon', offsetX: -W / 2 + P, offsetY: FEET_Y,
      polygonPoints: [0, -(H - L), W - 2 * P, -(H - L), W - 2 * P, 0, 0, 0],
      color: 0x3A0F2E, alpha: 1, depth: 93,
      tween: { scaleX: PACT_DOOR_LOOK.openLeafScale, alpha: 0, duration: holdMs, ease: 'Cubic.easeOut' },
    },
    post(-1),
    post(1),
    {
      type: 'rect', width: W + 8, height: L, offsetY: lintel + L / 2,
      color: PACT.fel, alpha: 0.95, depth: 94, glow: true, tween: fade,
    },
    {
      type: 'circle', radius: 5, offsetY: lintel - 18,
      color: PACT.ember, alpha: 0.95, depth: 96, glow: true,
      tween: { alpha: 0, scale: 1.6, duration: fadeMs, delay: holdMs, ease: 'Sine.easeOut' },
    },
    glintScatter({ count: 6, spread: 26, color: PACT.crimson, radius: 2.2, durationMs: 320 }),
  ];
}

/** Pact Door cast beat (symmetric: remote observers replay it without a facing). */
const PACT_DOOR_OPEN: VFXEffectConfig = {
  id: 'pact_door_open',
  name: 'Pact Door',
  category: 'burst',
  description: 'A door of fel fire opening, seen by other players when a warlock casts Pact Door',
  primitives: pactDoor(420, 520),
};

// ── Priest — Lightfall ──────────────────────────────────────────────────────

/** Priest holy palette (priest.toml): deep gold, soft gold, cream light. */
const LIGHT = { gold: 0xD4AC0D, soft: 0xF7DC6F, cream: 0xFFFACD } as const;

/** Lightfall lift — a ring of soft gold light under the feet and a short
 *  column of light carrying the priest up. Symmetric (remote cast beat). */
const LIGHTFALL_LIFT: VFXEffectConfig = {
  id: 'lightfall_lift',
  name: 'Lightfall Lift',
  category: 'burst',
  description: 'Gold ring and rising light as Lightfall lifts the priest',
  primitives: [
    {
      type: 'ellipse', width: 54, height: 14, offsetY: FEET_Y,
      color: LIGHT.soft, alpha: 0.7, depth: 93, glow: true,
      stroke: { color: LIGHT.cream, width: 2 },
      tween: { alpha: 0, scale: 2.0, duration: 460, ease: 'Sine.easeOut' },
    },
    { ...risingColumn({ width: 26, height: 70, color: LIGHT.cream, alpha: 0.45, glow: true,
      riseBy: 46, durationMs: 420 }), offsetY: FEET_Y - 35 },
    glintScatter({ count: 6, spread: 24, color: LIGHT.soft, radius: 2.2, durationMs: 300 }),
  ],
};

/** Lightfall float — the aura while drifting: a faint halo and gold-white
 *  motes rising off the priest like embers. Follows the player; the ability
 *  turns it off when the float ends. */
const LIGHTFALL_FLOAT: VFXEffectConfig = {
  id: 'lightfall_float',
  name: 'Lightfall Float',
  category: 'status',
  description: 'Halo and drifting motes while Lightfall floats',
  followTarget: 'player',
  primitives: [
    pulsingShell({
      width: 56, height: 64, color: LIGHT.soft, alpha: 0.32, glow: true,
      stroke: { color: LIGHT.cream, width: 2 },
      pulse: { scale: 1.1, alpha: 0.18, duration: 700 },
    }),
    driftMotes({
      direction: 'up', count: 6, spread: 20, radius: 3,
      color: LIGHT.cream, alpha: 0.95, glow: true, travel: 40, duration: 900,
    }),
    driftMotes({
      direction: 'up', count: 3, spread: 12, radius: 2,
      color: LIGHT.gold, alpha: 0.9, glow: true, travel: 30, duration: 1100, delay: 350,
    }),
    orbiter({
      radius: 3, color: LIGHT.cream, alpha: 0.9, glow: true, offsetY: -26,
      orbit: { radius: 12, speed: 0.6 },
      flicker: { alpha: 0.5, duration: 500 },
    }),
  ],
};

/** Lightfall landing — a puff of light where the priest touches down. */
const LIGHTFALL_LANDING: VFXEffectConfig = {
  id: 'lightfall_landing',
  name: 'Lightfall Landing',
  category: 'burst',
  description: 'Puff of light where Lightfall sets the priest down',
  primitives: [
    { ...groundCloud({ width: 48, height: 12, color: LIGHT.soft, alpha: 0.6, depth: 93,
      swellTo: 1.8, durationMs: 380 }), offsetY: FEET_Y },
    { ...glintScatter({ count: 5, spread: 20, color: LIGHT.cream, radius: 2, durationMs: 260 }), offsetY: FEET_Y - 6 },
  ],
};

// ── Hunter — Tetherline (D22) ───────────────────────────────────────────────

/** Ranger palette (HunterConfig colour, forest green), a hemp line, a pale
 *  leaf-green glint for the fletching. */
const TETHER = { green: 0x228B22, hemp: 0xE8D9A8, leaf: 0xB8F07A } as const;
const TL = CLASS_MOVEMENT_CONFIG.tetherline;

/** An arrowhead pointing along the line (bow → anchor), right-facing basis. */
function arrowhead(len: number, half: number): number[] {
  const a = Math.atan2(-TL.risePx, TL.distancePx);
  const pts = [len, 0, -len * 0.55, -half, -len * 0.2, 0, -len * 0.55, half];
  const out: number[] = [];
  for (let i = 0; i < pts.length; i += 2) {
    out.push(pts[i] * Math.cos(a) - pts[i + 1] * Math.sin(a), pts[i] * Math.sin(a) + pts[i + 1] * Math.cos(a));
  }
  return out;
}

/** The line, drawn FROM the anchor back to the bow and scaled toward the
 *  anchor over the reel: the pull is a straight line at constant speed, so
 *  the free end stays on the hunter. DashBehavior plays this def for the
 *  travel time (vfxOriginDurationMs), which also times the tween. */
function tetherLine(width: number, color: number, alpha: number, glow: boolean): VFXEffectConfig['primitives'][number] {
  return {
    type: 'line', linePoints: [{ x: 0, y: 0 }, { x: -TL.distancePx, y: TL.risePx }],
    offsetX: TL.distancePx, offsetY: -TL.risePx, lineWidth: width,
    color, alpha, depth: 96, ...(glow ? { glow: true } : {}),
    tween: { scale: 0, duration: TL.travelMs, ease: 'Linear' },
  };
}

/** Tetherline line — loosed at the cast: the line bow → anchor reeling in,
 *  the arrow bitten into the air at the anchor, a twang at the bow. */
const TETHERLINE_LINE: VFXEffectConfig = {
  id: 'tetherline_line',
  name: 'Tetherline',
  category: 'burst',
  description: 'The Tetherline from the hunter to its anchor, shortening as the hunter reels in',
  primitives: [
    tetherLine(7, TETHER.leaf, 0.3, true),
    tetherLine(2.5, TETHER.hemp, 0.95, false),
    {
      type: 'polygon', polygonPoints: arrowhead(11, 6), offsetX: TL.distancePx, offsetY: -TL.risePx,
      color: TETHER.hemp, alpha: 1, depth: 98,
    },
    {
      ...starFlash({ points: 4, innerRadius: 2, outerRadius: 12, color: TETHER.leaf, alpha: 0.9,
        glow: true, depth: 97, durationMs: TL.travelMs }),
      offsetX: TL.distancePx, offsetY: -TL.risePx,
    },
    ringBlast({ radius: 9, color: TETHER.hemp, alpha: 0.7, depth: 95,
      stroke: { color: TETHER.green, width: 2 }, expandTo: 2.2, durationMs: 220 }),
  ],
};

/** Tetherline release — the hunter lets go at the top: a green snap and
 *  leaf-feathers thrown off. Symmetric (also the remote cast beat). */
const TETHERLINE_RELEASE: VFXEffectConfig = {
  id: 'tetherline_release',
  name: 'Tetherline Release',
  category: 'burst',
  description: 'Snap and thrown leaf-feathers where Tetherline lets the hunter go',
  primitives: [
    ringBlast({ radius: 12, color: TETHER.leaf, alpha: 0.7, glow: true, depth: 94,
      stroke: { color: TETHER.green, width: 2 }, expandTo: 2.3, durationMs: 280 }),
    shardBurst({ count: 6, spread: 16, color: TETHER.green, alpha: 0.95, depth: 95,
      shard: LEAF, travel: 40, durationMs: 360 }),
    glintScatter({ count: 5, spread: 18, color: TETHER.hemp, radius: 2, durationMs: 260 }),
  ],
};

/** Tetherline trail fallback — a leaf glint, replayed per afterimage only
 *  when the ghost pool cannot spawn one (VFXSystem.onDashGhost; spawned at
 *  the body centre). Without it the fallback was the whole line def. */
const TETHERLINE_GLINT: VFXEffectConfig = {
  id: 'tetherline_glint',
  name: 'Tetherline Glint',
  category: 'burst',
  description: 'Leaf glint along the Tetherline reel (afterimage fallback)',
  primitives: [
    { type: 'circle', radius: 3,
      color: TETHER.leaf, alpha: 0.8, depth: 94, glow: true,
      tween: { alpha: 0, scale: 0.4, duration: 220, ease: 'Power2' } },
  ],
};

// ── Paladin — Divine Steed gallop (D23) ─────────────────────────────────────

/** Paladin gold (PaladinConfig colour), pale sun, white light. */
const STEED = { gold: 0xFFD700, pale: 0xFFF3B0, white: 0xFFFFF0 } as const;

/** Hoofbeat — a gold ring stamped at the feet with light kicked up. */
const HOOFBEAT = [
  { ...groundCloud({ width: 60, height: 12, color: STEED.gold, alpha: 0.55, depth: 93,
    swellTo: 1.7, durationMs: 380 }), offsetY: FEET_Y },
  { ...ringBlast({ radius: 12, color: STEED.pale, alpha: 0.8, glow: true, depth: 95,
    stroke: { color: STEED.gold, width: 2 }, expandTo: 2.4, durationMs: 300 }), offsetY: FEET_Y - 4 },
  { ...glintScatter({ count: 6, spread: 22, color: STEED.white, radius: 2.2, durationMs: 300 }), offsetY: FEET_Y - 8 },
];

/** Divine Steed charge — the steed of light bursts out: a hoofbeat and a
 *  rising flare of gold. Symmetric (also the remote cast beat). */
const DIVINE_STEED_CHARGE: VFXEffectConfig = {
  id: 'divine_steed_charge',
  name: 'Divine Steed Charge',
  category: 'burst',
  description: 'Hoofbeat of gold light where the Divine Steed gallop starts',
  primitives: [
    ...HOOFBEAT,
    { ...risingColumn({ width: 30, height: 56, color: STEED.pale, alpha: 0.4, glow: true,
      riseBy: 30, durationMs: 340 }), offsetY: FEET_Y - 28 },
  ],
};

/** Divine Steed arrival — the gallop pulls up: a last hoofbeat. */
const DIVINE_STEED_ARRIVAL: VFXEffectConfig = {
  id: 'divine_steed_arrival',
  name: 'Divine Steed Arrival',
  category: 'burst',
  description: 'Last hoofbeat where the Divine Steed gallop ends',
  primitives: [...HOOFBEAT],
};

/** Divine Steed hoofprint — the gallop's afterimage fallback (spawned at the
 *  body centre when the ghost pool cannot spawn one): a gold spark at
 *  the feet, not the whole charge def. */
const DIVINE_STEED_HOOF: VFXEffectConfig = {
  id: 'divine_steed_hoof',
  name: 'Divine Steed Hoofprint',
  category: 'burst',
  description: 'Gold spark at the feet along the Divine Steed gallop (afterimage fallback)',
  primitives: [
    { type: 'ellipse', width: 14, height: 4, ...LANDING,
      color: STEED.gold, alpha: 0.8, depth: 93, glow: true,
      tween: { alpha: 0, scale: 1.6, duration: 240, ease: 'Power2' } },
  ],
};

/** Divine Steed gallop — the run-speed buff: gold dust kicked up at the
 *  feet and a faint gold shell while it lasts. */
const DIVINE_STEED_GALLOP: VFXEffectConfig = {
  id: 'divine_steed_gallop',
  name: 'Divine Steed Gallop',
  category: 'status',
  description: 'Gold dust at the feet while Divine Steed speeds the paladin',
  followTarget: 'player',
  primitives: [
    pulsingShell({
      width: 52, height: 58, color: STEED.gold, alpha: 0.18, glow: true,
      stroke: { color: STEED.pale, width: 1.5 },
      pulse: { scale: 1.08, alpha: 0.1, duration: 420 },
    }),
    { ...driftMotes({
      direction: 'up', count: 5, spread: 18, radius: 2.4,
      color: STEED.pale, alpha: 0.9, glow: true, travel: 22, duration: 520,
    }), offsetY: FEET_Y - 4 },
  ],
};

// ── Mage — Slow Fall ────────────────────────────────────────────────────────

/** Mage arcane palette (mage.toml slow_fall: violet, lilac; aura sky blue). */
const ARCANE = { violet: 0x9B59B6, lilac: 0xD7BDE2, sky: 0xAACCFF } as const;

/** Slow Fall cushion — the aura while it lasts: a soft disc of arcane air
 *  under the feet and pale motes streaming up past the mage as they sink.
 *  Follows the player; the ability turns it off on landing. */
const SLOW_FALL_FLOAT: VFXEffectConfig = {
  id: 'slow_fall_float',
  name: 'Slow Fall',
  category: 'status',
  description: 'Arcane air cushioning the mage while Slow Fall lasts',
  followTarget: 'player',
  primitives: [
    pulsingShell({
      width: 58, height: 14, offsetY: FEET_Y + 2, color: ARCANE.sky, alpha: 0.4, glow: true,
      stroke: { color: ARCANE.lilac, width: 2 },
      pulse: { scale: 1.15, alpha: 0.2, duration: 600 },
    }),
    driftMotes({
      direction: 'up', count: 6, spread: 22, radius: 2.5,
      color: ARCANE.lilac, alpha: 0.9, glow: true, travel: 46, duration: 800,
    }),
    driftMotes({
      direction: 'up', count: 3, spread: 14, radius: 2,
      color: ARCANE.violet, alpha: 0.85, glow: true, travel: 34, duration: 1000, delay: 300,
    }),
  ],
};

export const MOVEMENT_ABILITY_VFX: VFXEffectConfig[] = [
  TETHERLINE_LINE,
  TETHERLINE_RELEASE,
  TETHERLINE_GLINT,
  DIVINE_STEED_CHARGE,
  DIVINE_STEED_HOOF,
  DIVINE_STEED_ARRIVAL,
  DIVINE_STEED_GALLOP,
  STORMSTRIDE_BOLT,
  STORMSTRIDE_SPARK,
  STORMSTRIDE_GLINT,
  HAREBOUND_SPRING,
  HAREBOUND_LANDING,
  HAREBOUND_LEAF,
  PACT_DOOR_OPEN,
  LIGHTFALL_LIFT,
  LIGHTFALL_FLOAT,
  LIGHTFALL_LANDING,
  SLOW_FALL_FLOAT,
];
